import { toRaw } from 'vue'

import { normalizeMoney, serializeMoney } from '@/lib/adapters/common.adapter'
import { normalizeImage } from '@/lib/adapters/image.adapters'
import {
  normalizeTrafficSourceCosts,
  serializeTrafficSourceCosts,
} from '@/lib/helpers/trafficSourceCosts.helpers'
import {
  type CampaignServerType,
  type CampaignType,
  CampaignTypeEnum,
  type InvitedUserType,
} from '@/types/campaign'

const handleDate = (value: string | null) => {
  return value === '0000-00-00 00:00:00' || value === '' ? null : value
}

const getCampaignType = (
  hasApproveCreatorsEnable: boolean,
  isAllowedToInviteCreators: boolean,
): CampaignTypeEnum => {
  if (!isAllowedToInviteCreators) {
    return CampaignTypeEnum.STORE_ACCEPT
  }
  return hasApproveCreatorsEnable ? CampaignTypeEnum.USER_ACCEPT : CampaignTypeEnum.STORE_ACCEPT
}

export const isEmailValid = (email: string) => {
  const regex = /^[\S]+@[\S]+\.[a-zA-Z]+$/
  return regex.test(email)
}

export const filterInvitedUsers = (users: InvitedUserType[]) =>
  users
    .filter((user) => user.type === 'email' && isEmailValid(user.identifier))
    .map((user) => user.identifier)

export type CampaignFormData = Omit<CampaignType, 'id' | 'image'> & {
  logoTemp?: Blob
  isAllowedToInviteCreators: boolean
}

export const serialiseCampaignForm = (
  data: CampaignFormData,
  storeGroups: number[],
  storeGroupId: number,
  currencyCode: string,
  skipTarget = false,
) => {
  const additionalPaymentAmount = data.additionalPayment
  const tagIds = toRaw(data.tagIds)
  let logoFile = null
  const traffic_sources_costs = serializeTrafficSourceCosts(
    data.groupedTrafficSourceCosts,
    currencyCode,
  )
  const type =
    data.type || getCampaignType(data.hasApproveCreatorsEnable, data.isAllowedToInviteCreators)

  const additionalPaymentProps = data.hasAdditionalPayment
    ? {
        additional_payment: {
          mode: 'INFER_REVENUE_FROM_COST',
          cost_creator: serializeMoney({
            amount: additionalPaymentAmount,
            currency: currencyCode,
          }),
        },
      }
    : { has_onetime_payment: false }
  const body = {
    campaign_title: data.title,
    campaign_text: data.description,
    has_product_seeding: data.hasProductSeeding,
    ...(data.todoList.length && { todo: data.todoList.map((item) => item.value) }),
    ...additionalPaymentProps,
    valid_from: handleDate(data.startDate),
    valid_until: handleDate(data.endDate),
    hashtags: type === CampaignTypeEnum.STANDARD ? [] : (data.hashtags ?? []),
    max_clicks: data.maxClicks || null,
    per_user_limit: data.hasIndividualLimitPerUserEnable,
    store_group_id: storeGroupId,
    type,
    ...(traffic_sources_costs.length && { traffic_sources_costs }),
    ...(!skipTarget && {
      targets: {
        user_tag_ids: tagIds?.length ? tagIds : [],
        store_group_ids: data.hasInviteMetapicCreatorsEnable ? storeGroups : [],
        emails: filterInvitedUsers(data.invitedUsers || []),
        ...(data.segments?.length && { segment_ids: data.segments.map(({ id }) => id) }),
      },
    }),
  }

  if (data.logoTemp) {
    logoFile = new FormData()
    logoFile.append('default', data.logoTemp)
  }

  return {
    body,
    logoFile,
  }
}

export const normalizeCampaignServerData = (data: CampaignServerType): CampaignType => {
  const additionalPaymentSource =
    data.additional_payment?.cost_creator ?? data.additional_payment?.revenue_invoice
  const additionalPayment = additionalPaymentSource ? normalizeMoney(additionalPaymentSource) : null
  const revenueInvoice = data.additional_payment?.revenue_invoice
  return {
    id: data.id,
    title: data.campaign_title,
    description: data.campaign_text,
    type: data.type,
    ...(data.image && { image: normalizeImage(data.image) }),
    hasTodoListEnable: !!data.todo?.length,
    todoList: (data.todo || []).map((value, index) => ({ key: index, value })),
    startDate: handleDate(data.constraints.valid_from),
    endDate: handleDate(data.constraints.valid_until),
    hasStartNow: !data.constraints.valid_from,
    hasOpenEnd:
      !data.constraints.valid_until && !data.constraints.max_clicks && !data.constraints.max_money,
    hashtags: data.hashtags ?? [],
    hasProductSeeding: !!data.has_product_seeding,
    maxClicks: data.constraints.max_clicks,
    maxBudget: data.constraints.max_money,
    hasIndividualLimitPerUserEnable: data.constraints.per_user_limit,
    hasAdditionalPayment: Boolean(data.additional_payment),
    additionalPayment: additionalPayment?.amount ?? 0,
    ...(revenueInvoice && {
      additionalPaymentRevenueInvoice: normalizeMoney(revenueInvoice).amount,
    }),
    hasCommissionEnable: !!data.traffic_sources_costs?.length,
    groupedTrafficSourceCosts: normalizeTrafficSourceCosts(data.traffic_sources_costs),
    hasInviteMetapicCreatorsEnable: !!data.store_group_id, // fixme should check user permission instead
    hasApproveCreatorsEnable: data.type === CampaignTypeEnum.STORE_ACCEPT,
    token: data.token,
    advertiserId: data.store_id,
    advertiserLogo: data.store_logo ? normalizeImage(data.store_logo) : null,
    advertiserName: data.store_name || '',
  }
}
